import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useConsumption } from '../../contexts/ConsumptionContext';

/**
 * WaterGoalPage - Página de configuração de meta de água
 * 
 * Permite ao usuário definir sua meta de consumo de água usando um slider.
 * Endpoint GET: /consumo/agua/:id/meta/api_data
 * Endpoint PATCH: /consumo/agua/:id/meta
 */
export function WaterGoalPage() {
  const { id } = useParams<{ id: string }>();
  const { currentResidenceId, setCurrentResidenceId } = useConsumption();
  const { t } = useTranslation();
  
  const residenceId = id || currentResidenceId;
  
  const [goalValue, setGoalValue] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Atualiza o contexto se a URL mudou
  useEffect(() => {
    if (id && id !== currentResidenceId) {
      setCurrentResidenceId(id);
    }
  }, [id, currentResidenceId, setCurrentResidenceId]);

  // Carrega o valor atual da meta
  useEffect(() => {
    const fetchGoal = async () => {
      try {
        const response = await api.get(`/consumo/agua/${residenceId}/meta/api_data`);
        setGoalValue(response.data.water_goal || 0);
      } catch (err) {
        console.error('Erro ao carregar meta:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (residenceId) {
      fetchGoal();
    }
  }, [residenceId]);

  // Salva a meta com debounce
  const saveGoal = useCallback(async (value: number) => {
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      await api.patch(`/consumo/agua/${residenceId}/meta`, {
        water_goal: value
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Erro ao salvar meta:', err);
    } finally {
      setIsSaving(false);
    }
  }, [residenceId]);

  // Handler do slider com debounce para salvar
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setGoalValue(value);
    
    // Debounce: aguarda 500ms após parar de arrastar
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveGoal(value);
    }, 500);
  };

  // Limpa timeout ao desmontar
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <div className="w-full md:w-1/2 px-[15px]">
      <div className="p-4">
        <div className="mb-4">
          <h4 className="text-[#7b8aa4] text-[18px] font-['Poppins',sans-serif] font-medium">
            {t('views.layouts.goal.config', 'Configuração de meta')}
          </h4>
        </div>
          
          {/* Slider estilo Bootstrap original */}
          <div className="mt-12 px-4">
            <div className="relative">
              {/* Tooltip do valor - estilo original */}
              <div 
                className="absolute -top-10 transform -translate-x-1/2 bg-white text-[#333] text-[14px] px-3 py-1.5 rounded shadow-[0_5px_20px_rgba(0,0,0,0.1)] z-10"
                style={{ left: `${goalValue}%` }}
              >
                {goalValue}m³
                {/* Seta do tooltip */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-white"></div>
              </div>
              
              {/* Container do slider */}
              <div className="relative py-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={goalValue}
                  onChange={handleSliderChange}
                  className="
                    relative w-full h-[10px] rounded-full appearance-none cursor-pointer
                    bg-[#eee] shadow-[0_0_0_10px_white]
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-[20px]
                    [&::-webkit-slider-thumb]:h-[20px]
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-white
                    [&::-webkit-slider-thumb]:border-[4px]
                    [&::-webkit-slider-thumb]:border-[#2988b5]
                    [&::-webkit-slider-thumb]:cursor-pointer
                    [&::-webkit-slider-thumb]:shadow-[0_5px_20px_rgba(0,0,0,0.1)]
                    [&::-webkit-slider-thumb]:relative
                    [&::-webkit-slider-thumb]:z-10
                    [&::-moz-range-thumb]:w-[20px]
                    [&::-moz-range-thumb]:h-[20px]
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-white
                    [&::-moz-range-thumb]:border-[4px]
                    [&::-moz-range-thumb]:border-[#2988b5]
                    [&::-moz-range-thumb]:cursor-pointer
                    [&::-moz-range-track]:bg-[#eee]
                    [&::-moz-range-track]:rounded-full
                    [&::-moz-range-track]:h-[10px]
                  "
                  style={{
                    background: `linear-gradient(to right, #2988b5 0%, #2988b5 ${goalValue}%, #eee ${goalValue}%, #eee 100%)`
                  }}
                />
              </div>
              
              {/* Labels 0 e 100 nas extremidades */}
              <div className="flex justify-between text-[12px] text-[#737373] mt-6">
                <span>0</span>
                <span>100</span>
              </div>
            </div>
          </div>
          
          <div className="mt-8">
            <p className="text-[#666] text-[14px]">
              {t('views.consumption.goal.message', 'Este valor é apenas uma projeção baseado no custo médio por m³ dos últimos 3 meses. O valor do m³ varia de acordo com o consumo total do condomínio.')}
            </p>
          </div>
      </div>
      
      {/* Mensagem de sucesso ao salvar */}
      {saveSuccess && (
        <div 
          className="
            relative
            pl-[60px] pr-12 py-[12px]
            !bg-[#2dcc6f] text-white
            rounded
            overflow-hidden
            after:content-[''] after:absolute after:w-[57px] after:h-[52px]
            after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
            after:top-[14px] after:left-[5px]
            after:bg-[position:-58px_-166px]
          " 
          role="alert"
        >
          <button 
            type="button" 
            className="absolute top-[11px] right-[15px] p-0 bg-transparent border-0 text-[21px] font-bold leading-none cursor-pointer"
            onClick={() => setSaveSuccess(false)}
            aria-label="Fechar"
          >
            <span className="text-white text-[28px] font-black opacity-100 hover:opacity-100">&times;</span>
          </button>
          <strong className="block font-bold">{t('views.consumption.goal.success_title', 'Sucesso!')}</strong>
          <small className="text-[80%] font-normal">
            {t('views.consumption.goal.configuration_saved', 'Configuração salva com sucesso.')}
          </small>
        </div>
      )}
      
      {/* Indicador de salvando */}
      {isSaving && (
        <div className="text-center text-[#999] mt-4">
          <span className="text-[14px]">{t('views.consumption.goal.saving', 'Salvando...')}</span>
        </div>
      )}
    </div>
  );
}
